import { useState, useEffect, useMemo, useCallback } from "react";
import { canEditAgent } from "../../../utils/agentRbac";
import { useNavigate } from "react-router-dom";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import { useAuth } from "../../../contexts/AuthContext";
import OverviewTab from "./OverviewTab";
import GraphTab from "./GraphTab";
import PipelineTab from "./PipelineTab";
import RoutingExamplesPanel from "./RoutingExamplesPanel";
import WiringTab from "./WiringTab";
import ActivityTab from "./ActivityTab";
import VersionsTab from "./VersionsTab";
import KnowledgeTab from "./KnowledgeTab";
import LifecycleTab from "./LifecycleTab";
import GraphNodeDrawer from "./edit/GraphNodeDrawer";
import PlatformManagedView from "./edit/PlatformManagedView";
import { isPlatformManagedAgent } from "./edit/platformManagedAgent";
import AgentAvatar from "../iphone-grid/AgentAvatar";
import AvatarPicker from "../iphone-grid/AvatarPicker";
import EmbedSnippetModal from "./EmbedSnippetModal";

// Phase B per agent-detail-viewer-v1.md §10. Shell only — tabs are stubbed
// here and filled in by B7-B11/B13. Pipeline tab is supervisor-only.
// Platform-managed agents (Super Agent / Platform Tester / Orchestrator)
// render <PlatformManagedView> instead of the full tabbed surface
// per the post-shipping clarification: only LLM provider + model
// editable; system_prompt + flow_config + tree shape stay opaque
// because they're platform IP.

const Badge = ({ children, color }) => (
  <span style={{
    display: "inline-block", padding: "3px 10px", borderRadius: 6,
    fontSize: 10, fontWeight: 600, background: `${color}18`, color,
    border: `1px solid ${color}30`, letterSpacing: "0.04em",
    textTransform: "uppercase",
  }}>{children}</span>
);

const STATUS_COLOR = { live: COLORS.green, paused: "#F59E0B", draft: "#6B7280" };
const KIND_COLOR = { supervisor: COLORS.purple, regular: COLORS.cyan };

// Phase 3 helpers — deep-merge for staged patches + leaf counter
// for the "N unsaved changes" pill. Exported for direct unit coverage.
export function deepMerge(dst, src) {
  if (src === null || src === undefined) return dst;
  if (Array.isArray(src)) return src;  // arrays replace, don't merge
  if (typeof src !== "object") return src;
  const out = { ...(dst && typeof dst === "object" && !Array.isArray(dst) ? dst : {}) };
  for (const k of Object.keys(src)) {
    const dv = out[k];
    const sv = src[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv)
        && dv && typeof dv === "object" && !Array.isArray(dv)) {
      out[k] = deepMerge(dv, sv);
    } else {
      out[k] = sv;
    }
  }
  return out;
}

export function countLeaves(obj) {
  if (obj === null || obj === undefined) return 0;
  if (Array.isArray(obj)) return 1;  // an array assignment counts as one change
  if (typeof obj !== "object") return 1;
  let n = 0;
  for (const v of Object.values(obj)) n += countLeaves(v);
  return n;
}

// B51 (2026-07-23): "versions" tab — history + rollback + clone. The
// backend endpoints existed unwired; users reported the capability missing.
// B7 — "lifecycle" tab: the Template→Run→Evaluate→Improve→Promote→Observe
// capability loop presented as one journey, each stage deep-linking to its
// real surface. Logic in capabilityLoopStages.js; LifecycleTab is a thin shell.
const TABS_REGULAR    = ["overview", "graph", "wiring", "activity", "knowledge", "lifecycle", "versions"];
// ASC-v3 RH-UI: "routing" surfaces per-expert Routing Examples as an agent
// property (voice warm-handoff + chat L1.5), supervisor-only.
const TABS_SUPERVISOR = ["overview", "graph", "pipeline", "routing", "wiring", "activity", "knowledge", "lifecycle", "versions"];
// B19 — business_user role narrows to Overview + Activity per design row 19.
const TABS_BUSINESS_USER = ["overview", "activity"];
const TAB_LABEL = {
  overview: "Overview", graph: "Graph", pipeline: "Pipeline", routing: "Routing",
  wiring: "Models", activity: "Activity", knowledge: "Knowledge",
  lifecycle: "Capability loop", versions: "Versions",
};

export default function AgentViewer({ agentId, onBack, onEditAgent, onSelectAgent }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [drawer, setDrawer] = useState(null);  // {kind, payload} | null
  const [showUpdatePanel, setShowUpdatePanel] = useState(false);
  // B11 — stale_tree_version conflict banner.
  const [conflict, setConflict] = useState(null);
  const [refetchTick, setRefetchTick] = useState(0);
  // Phase 3 — explicit Save button + dirty state.
  // pendingByAgent: { [agentId]: <partial-patch deep-merged> }.
  // Each EditableField call routes its patch into the right agent's
  // pending bucket via stageChange(); the Save button commits all
  // buckets in one go.
  const [pendingByAgent, setPendingByAgent] = useState({});
  const [committing, setCommitting] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  // "Copy embed snippet" modal — mints a fresh widget-scoped API key
  // tied to this agent and produces a ready-to-paste <script> tag.
  const [embedModalOpen, setEmbedModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDrawer(null);
    setActiveTab("overview");
    setPendingByAgent({});  // discard staged changes on agent switch
    agentApi.getTree(agentId)
      .then(res => { if (!cancelled) setTree(res?.data || null); })
      .catch(e => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentId, refetchTick]);

  // Stage a change against a specific agent row. Default target = the
  // tree's primary agent. Tabs that operate on experts (Graph drawer
  // expert nodes, Pipeline L1.5 exemplars editor) pass agentId
  // explicitly. Stage is sync — no API call, no throw.
  const stageChange = useCallback((updates, targetAgentId) => {
    if (!tree?.agent?.id) return;
    const aid = targetAgentId || tree.agent.id;
    setPendingByAgent(prev => ({
      ...prev,
      [aid]: deepMerge(prev[aid] || {}, updates),
    }));
  }, [tree]);

  // Backwards-compat wrapper: the prop name `saveAgent` is what every
  // tab + drawer expects today (autosave model). With Phase 3 it stages
  // instead of calling the API. Resolves immediately so EditableField's
  // optimistic-success path still works.
  const saveAgent = useCallback(async (updates, targetAgentId) => {
    stageChange(updates, targetAgentId);
  }, [stageChange]);

  const dirtyCount = useMemo(() => countLeaves(pendingByAgent), [pendingByAgent]);
  const isDirty = dirtyCount > 0;

  // Discard all staged changes; tree displayed reverts to server state.
  const discardChanges = useCallback(() => {
    setPendingByAgent({});
  }, []);

  // Commit all staged patches across all agents. One API call per
  // dirty row. On any 412, surface conflict banner; partial commits
  // (some succeed, some fail) leave failed buckets staged so user can
  // retry after refresh.
  const commitChanges = useCallback(async () => {
    if (!isDirty || !tree) return;
    setCommitting(true);
    setError(null);
    const stillStaged = {};
    let firstStaleErr = null;
    let firstOtherErr = null;

    for (const [aid, patch] of Object.entries(pendingByAgent)) {
      const target = aid === tree.agent.id
        ? tree.agent
        : (tree.experts || []).find(e => e.id === aid);
      if (!target) continue;
      try {
        await agentApi.update(aid, patch, {
          expectedTreeVersion: target.tree_version,
          expectedUpdatedAt: target.updated_at,
        });
      } catch (err) {
        stillStaged[aid] = patch;
        if (err?.status === 412 && err?.body?.detail?.error?.startsWith("stale_") && !firstStaleErr) {
          firstStaleErr = err;
        } else if (!firstOtherErr) {
          firstOtherErr = err;
        }
      }
    }

    if (firstStaleErr) {
      // firstStaleErr is only set when body.detail.error started with "stale_"
      // (see the detection above), so body.detail is guaranteed present.
      const detail = firstStaleErr.body.detail;
      setConflict({
        clientVersion: detail.client_version,
        currentVersion: detail.current_version,
        currentUpdatedAt: detail.current_updated_at,
      });
    } else if (firstOtherErr) {
      setError(firstOtherErr?.message || String(firstOtherErr));
    }

    setPendingByAgent(stillStaged);
    setRefetchTick(t => t + 1);
    setCommitting(false);
  }, [isDirty, tree, pendingByAgent]);

  // Tab-switch: emit Esc-equivalent for unsaved-warn; here we keep the
  // staged patch alive when the user navigates between tabs (the design
  // says the dirty state survives tab switches; only Discard or browser
  // navigate-away clears it).

  // Browser-leave warning when dirty.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";  // legacy browsers
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // Build the displayed tree by deep-merging staged patches over the
  // server tree. Tabs render against this so users see their staged
  // edits live.
  const displayedTree = useMemo(() => {
    if (!tree) return tree;
    const supervisorId = tree.agent?.id;
    const supPatch = supervisorId ? (pendingByAgent[supervisorId] || null) : null;
    return {
      ...tree,
      agent: supPatch ? deepMerge(tree.agent, supPatch) : tree.agent,
      experts: (tree.experts || []).map(e =>
        pendingByAgent[e.id] ? deepMerge(e, pendingByAgent[e.id]) : e
      ),
    };
  }, [tree, pendingByAgent]);

  const agent = tree?.agent || null;
  const kind  = tree?.agent_kind || agent?.metadata_json?.agent_kind || "regular";
  const isBusinessUser = currentUser?.role === "business_user";
  const tabs = useMemo(() => {
    if (isBusinessUser) return TABS_BUSINESS_USER;
    return kind === "supervisor" ? TABS_SUPERVISOR : TABS_REGULAR;
  }, [kind, isBusinessUser]);
  // If the active tab gets hidden by an RBAC change (e.g. role refresh
  // mid-session), bounce the user back to Overview rather than render an
  // unreachable tab body.
  useEffect(() => {
    if (!tabs.includes(activeTab)) setActiveTab("overview");
  }, [tabs, activeTab]);

  // Drawer auto-closes on tab switch (per §12 hostile audit row 4: don't
  // carry stale node identity across axes).
  useEffect(() => { setDrawer(null); }, [activeTab]);

  // B17 — keyboard nav. Esc always closes the drawer; ←/→ traverse
  // siblings when the open drawer carries a `siblings` array + cursor
  // (Overview expert tree, Graph node tiles, etc.).
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setDrawer(null);
        return;
      }
      const sibs = drawer.siblings;
      if (!Array.isArray(sibs) || sibs.length < 2) return;
      const idx = drawer.index ?? 0;
      let next = null;
      if (e.key === "ArrowRight") next = (idx + 1) % sibs.length;
      else if (e.key === "ArrowLeft") next = (idx - 1 + sibs.length) % sibs.length;
      if (next == null) return;
      e.stopPropagation();
      const target = sibs[next];
      if (!target) return;
      setDrawer({ ...target, siblings: sibs, index: next });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  // Platform-managed agents (Super Agent / Platform Tester / Orchestrator)
  // get a stripped-down view with only provider + model editable.
  // Render BEFORE the early returns for loading/error/missing so the
  // header still renders correctly during refetch.
  if (!loading && !error && tree?.agent && isPlatformManagedAgent(tree.agent)) {
    return (
      <PlatformManagedView
        tree={displayedTree}
        saveAgent={saveAgent}
        onBack={onBack}
        currentUser={currentUser}
        isDirty={isDirty}
        dirtyCount={dirtyCount}
        onCommit={commitChanges}
        onDiscard={discardChanges}
        committing={committing}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header card — thickGlass (R3.1). 88% opacity floor keeps the
          name + status badges legible while picking up the gradient
          backdrop's hue variation through the blur. */}
      <div style={{
        ...MATERIALS.thickGlass(),
        borderRadius: 0,
        borderBottom: `1px solid ${COLORS.border}`,
        borderTop: "none", borderLeft: "none", borderRight: "none",
        padding: "16px 24px",
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <button onClick={onBack} style={{
          background: "transparent", border: `1px solid ${COLORS.border}`,
          color: COLORS.textDim, padding: "6px 12px", borderRadius: 8,
          cursor: "pointer", fontSize: 12,
        }}>{"← Back"}</button>

        {/* Clickable avatar — page-level identity + entry point for the
            color/icon picker. business_user role keeps the avatar but
            non-clickable. The "✎" overlay surfaces on hover so the
            change-avatar affordance is discoverable without copy. */}
        {agent && (
          <button
            type="button"
            onClick={() => !isBusinessUser && setAvatarPickerOpen(true)}
            disabled={isBusinessUser}
            title={isBusinessUser ? agent.name : "Change avatar"}
            style={{
              position: "relative",
              background: "transparent", border: "none", padding: 0,
              cursor: isBusinessUser ? "default" : "pointer",
              borderRadius: 14, display: "flex",
              transition: "transform 0.15s",
            }}
            onMouseEnter={(e) => {
              // The button is `disabled` for business_user, so these handlers
              // only ever fire for editors — where the overlay span (below) is
              // always rendered. No business-user / null guard needed.
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.querySelector("[data-edit-overlay]").style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.querySelector("[data-edit-overlay]").style.opacity = "0";
            }}
          >
            <AgentAvatar agent={agent} size={44} variant="tile" />
            {!isBusinessUser && (
              <span
                data-edit-overlay
                style={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(0,0,0,0.45)", color: "#fff",
                  fontSize: 14, fontWeight: 700, borderRadius: 14,
                  opacity: 0, transition: "opacity 0.15s",
                  pointerEvents: "none",
                }}
              >✎</span>
            )}
          </button>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
              {loading ? "Loading…" : (agent?.name || "(unknown agent)")}
            </div>
            {agent?.status && <Badge color={STATUS_COLOR[agent.status] || COLORS.textDim}>{agent.status}</Badge>}
            <Badge color={KIND_COLOR[kind] || COLORS.textDim}>{kind}</Badge>
            {tree?.update_available && (
              <button onClick={() => setShowUpdatePanel(prev => !prev)}
                title="Click for version diff + install new version"
                style={{
                  padding: "3px 10px", borderRadius: 6,
                  fontSize: 10, fontWeight: 700,
                  background: showUpdatePanel ? "#F59E0B33" : "#F59E0B22",
                  color: "#F59E0B", border: "1px solid #F59E0B55",
                  letterSpacing: "0.04em", textTransform: "uppercase",
                  cursor: "pointer",
                }}>{"⚡ Update available"}</button>
            )}
          </div>
          {agent?.metadata_json?.description && (
            <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 4 }}>
              {agent.metadata_json.description}
            </div>
          )}
        </div>

        {/* Phase 3 — Save / Discard buttons + dirty count.
            Visible only when there are staged changes. Save commits
            all rows; Discard reverts to server state. Stays in the
            header so it's always reachable across tab switches. */}
        {isDirty && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              padding: "3px 10px", borderRadius: 999, fontSize: 11,
              background: `${COLORS.orange}22`, color: COLORS.orange,
              border: `1px solid ${COLORS.orange}55`, fontWeight: 600,
            }}>{`${dirtyCount} unsaved`}</span>
            <button onClick={discardChanges} disabled={committing}
              style={{
                background: "transparent", border: `1px solid ${COLORS.border}`,
                color: COLORS.textDim, padding: "6px 12px", borderRadius: 8,
                cursor: committing ? "default" : "pointer", fontSize: 11, fontWeight: 600,
                opacity: committing ? 0.55 : 1,
              }}>Discard</button>
            <button onClick={commitChanges} disabled={committing}
              style={{
                background: COLORS.accent, border: "none",
                color: "#fff", padding: "6px 14px", borderRadius: 8,
                cursor: committing ? "default" : "pointer", fontSize: 11, fontWeight: 600,
                opacity: committing ? 0.55 : 1,
              }}>{committing ? "Saving…" : "Save changes"}</button>
          </div>
        )}

        {/* B20 — bidirectional contract with Builder. Click takes the user
            to /builder with the agent loaded; returning to the Viewer
            triggers a fresh /tree fetch via the agentId-keyed useEffect,
            so any edit (rename, toolset opt-in/out, structural save) shows
            up without stale state. Hidden when no callback is wired. */}
        {agent?.id && onEditAgent && (
          <button onClick={() => onEditAgent(tree.agent)} style={{
            background: "transparent", border: `1px solid ${COLORS.border}`,
            color: COLORS.text, padding: "6px 12px", borderRadius: 8,
            cursor: "pointer", fontSize: 11, fontWeight: 600,
          }}>{"Open in Builder →"}</button>
        )}

        {agent?.id && (
          <button
            onClick={() => setEmbedModalOpen(true)}
            title="Copy a ready-to-paste <script> tag that embeds this agent on any website"
            style={{
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.text, padding: "6px 12px", borderRadius: 8,
              cursor: "pointer", fontSize: 11, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <span style={{ opacity: 0.85, fontFamily: "monospace", fontSize: 10 }}>{"</>"}</span>
            Widget
          </button>
        )}

        {agent?.id && (
          <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }} title={agent.id}>
            {`Agent ID: ${agent.id}`}
          </div>
        )}
      </div>

      {/* Embed snippet generator — wraps widgetApi.createKey + clipboard write */}
      <EmbedSnippetModal
        open={embedModalOpen}
        onClose={() => setEmbedModalOpen(false)}
        agent={tree?.agent}
      />

      {/* B11 — stale_tree_version conflict banner. Triggered when a
          field-edit save returns 412 (someone else edited the agent
          in another tab / Builder). Refreshing reloads /tree and
          clears the banner so the user can re-attempt with a fresh
          baseline. */}
      {conflict && (
        <div style={{
          padding: "10px 24px", background: `${COLORS.orange}1A`,
          borderBottom: `1px solid ${COLORS.orange}55`,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.orange, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Out of date
          </span>
          <span style={{ fontSize: 12, color: COLORS.text, flex: 1, minWidth: 200 }}>
            Someone else (or another tab) edited this agent — your last save was rejected.
            Refresh to load the latest version, then re-apply your edit.
            {conflict.currentVersion != null && (
              <span style={{ color: COLORS.textDim, marginLeft: 6, fontFamily: "monospace", fontSize: 11 }}>
                (v{conflict.clientVersion} → v{conflict.currentVersion})
              </span>
            )}
          </span>
          <button
            onClick={() => { setConflict(null); setRefetchTick(t => t + 1); }}
            style={{
              padding: "4px 12px", borderRadius: 6, border: "none",
              background: COLORS.orange, color: "#fff",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}>Refresh</button>
        </div>
      )}

      {/* B18 — Retired baseline banner. Driven by metadata_json.template_status === 'retired' */}
      {agent?.metadata_json?.template_status === "retired" && (
        <div style={{
          padding: "10px 24px", background: `${COLORS.pink}18`,
          borderBottom: `1px solid ${COLORS.pink}55`,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.pink, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Retired baseline
          </span>
          <span style={{ fontSize: 12, color: COLORS.text }}>
            The marketplace template this agent was cloned from has been retired
            by the platform team. Existing flows continue to run; install a
            replacement to stay current.
          </span>
        </div>
      )}

      {/* B18 — Update available details panel */}
      {showUpdatePanel && tree?.update_available && tree?.template_baseline && (
        <div style={{
          padding: "12px 24px", background: `${COLORS.orange}12`,
          borderBottom: `1px solid ${COLORS.orange}55`,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.orange, letterSpacing: "0.04em" }}>
                A newer version of this template is available
              </div>
              <div style={{ fontSize: 11, color: COLORS.text, marginTop: 4, lineHeight: 1.5 }}>
                Side-by-side install per marketplace-module-v1 versioning policy: the
                catalog opens with the slug pre-selected; the new install lands as a
                versioned name (e.g. <code>data_analyst-v2</code>) so this agent stays untouched.
              </div>
            </div>
            <button onClick={() => {
                const slug = tree?.template_baseline?.template_id || "";
                navigate(`/marketplace?install=${encodeURIComponent(slug)}`);
              }}
              style={{
                padding: "6px 14px", borderRadius: 8, border: "none",
                background: COLORS.orange, color: "#fff",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>Install new version →</button>
            <button onClick={() => setShowUpdatePanel(false)} style={{
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.textDim, padding: "5px 12px", borderRadius: 8,
              cursor: "pointer", fontSize: 11,
            }}>Dismiss</button>
          </div>
          <pre style={{
            margin: 0, padding: 10, borderRadius: 8,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface,
            fontSize: 10, color: COLORS.text, fontFamily: "monospace",
            overflow: "auto",
          }}>
{`baseline_hash : ${tree.template_baseline.snapshot_hash}
current_hash  : ${tree.current_hash || "(unknown)"}
schema_version: ${tree.template_baseline.schema_version} → ${tree.current_version || "(unknown)"}
installed_at  : ${tree.template_baseline.installed_at}
template_id   : ${tree.template_baseline.template_id}`}
          </pre>
        </div>
      )}

      {/* Tab strip — thickGlass continuation of the header band (R3.1). */}
      <div style={{
        ...MATERIALS.thickGlass(),
        borderRadius: 0,
        borderBottom: `1px solid ${COLORS.border}`,
        borderTop: "none", borderLeft: "none", borderRight: "none",
        padding: "0 24px",
        display: "flex", gap: 4,
      }}>
        {tabs.map(t => {
          const active = activeTab === t;
          return (
            <button key={t} onClick={() => setActiveTab(t)} style={{
              background: "transparent", border: "none", color: active ? COLORS.text : COLORS.textDim,
              padding: "10px 14px", cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 500,
              borderBottom: `2px solid ${active ? COLORS.accent : "transparent"}`,
              transition: "color 0.15s, border-color 0.15s",
            }}>{TAB_LABEL[t]}</button>
          );
        })}
      </div>

      {/* AvatarPicker — opened from the header avatar. Writes through
          saveAgent which stages the patch into pendingByAgent; user
          commits via the existing Save-changes button. */}
      {avatarPickerOpen && agent && (
        // Bind to `displayedTree.agent` (server state + staged patches)
        // not the bare `tree.agent`. Otherwise the picker preview never
        // reflects in-flight color/icon picks until the user clicks Save.
        <AvatarPicker
          agent={displayedTree.agent}
          value={displayedTree.agent?.metadata_json?.icon}
          onChange={(v) => saveAgent({ metadata_json: { icon: v } })}
          onClose={() => setAvatarPickerOpen(false)}
        />
      )}

      {/* Body */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
          {loading && <div style={{ color: COLORS.textDim }}>Loading…</div>}
          {!loading && error && (
            <div style={{ color: "#EF4444" }}>Failed to load agent: {error}</div>
          )}
          {!loading && !error && !agent && (
            <div style={{ color: COLORS.textDim }}>Agent not found.</div>
          )}
          {!loading && !error && agent && (
            <TabBody
              tab={activeTab}
              tree={displayedTree}
              currentUser={currentUser}
              onOpenDrawer={setDrawer}
              saveAgent={saveAgent}
              onSelectAgent={onSelectAgent}
              onChannelsChanged={() => setRefetchTick(t => t + 1)}
              setActiveTab={setActiveTab}
            />
          )}
        </div>

        {/* Inspector drawer */}
        {drawer && (
          <div style={{
            width: 380, borderLeft: `1px solid ${COLORS.border}`,
            background: COLORS.surface, padding: 20, overflow: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.06em", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {drawer.kind}
              </div>
              {Array.isArray(drawer.siblings) && drawer.siblings.length > 1 && (
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button title="Previous (←)"
                    onClick={() => {
                      const sibs = drawer.siblings;
                      const idx = drawer.index ?? 0;
                      const next = (idx - 1 + sibs.length) % sibs.length;
                      setDrawer({ ...sibs[next], siblings: sibs, index: next });
                    }}
                    style={{
                      background: "transparent", border: `1px solid ${COLORS.border}`,
                      color: COLORS.textDim, cursor: "pointer", fontSize: 12,
                      padding: "2px 8px", borderRadius: 5,
                    }}>{"←"}</button>
                  <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                    {(drawer.index ?? 0) + 1}/{drawer.siblings.length}
                  </span>
                  <button title="Next (→)"
                    onClick={() => {
                      const sibs = drawer.siblings;
                      const idx = drawer.index ?? 0;
                      const next = (idx + 1) % sibs.length;
                      setDrawer({ ...sibs[next], siblings: sibs, index: next });
                    }}
                    style={{
                      background: "transparent", border: `1px solid ${COLORS.border}`,
                      color: COLORS.textDim, cursor: "pointer", fontSize: 12,
                      padding: "2px 8px", borderRadius: 5,
                    }}>{"→"}</button>
                </div>
              )}
              <button title="Close (Esc)" onClick={() => setDrawer(null)} style={{
                background: "transparent", border: "none", color: COLORS.textDim,
                cursor: "pointer", fontSize: 16,
              }}>{"✕"}</button>
            </div>
            {/* B2-B5/B7-B8 — Graph node payloads route to GraphNodeDrawer
                so each node kind gets a dedicated editable surface.
                Other kinds (e.g. "Expert: ...") fall back to JSON. */}
            {drawer.payload?.node && drawer.payload?.agent_id ? (
              <GraphNodeDrawer
                node={drawer.payload.node}
                agent={
                  // Resolve the live agent so the drawer reads fresh
                  // values after each save. Must read from
                  // `displayedTree` (server tree deep-merged with
                  // staged pendingByAgent patches) — every other tab
                  // renders off displayedTree via TabBody (line ~602),
                  // and the Save button is what commits staged patches
                  // to the server; reading raw `tree` here meant an
                  // edit only appeared once Save triggered a refetch,
                  // not the instant the field itself changed.
                  // Supervisor is displayedTree.agent; experts are in
                  // displayedTree.experts. Falls back to the payload's
                  // snapshot if the resolution fails (e.g. ID mismatch
                  // after a tree shape change).
                  drawer.payload.agent_id === displayedTree.agent.id
                    ? displayedTree.agent
                    : displayedTree.experts.find(e => e.id === drawer.payload.agent_id)
                      || drawer.payload
                }
                saveAgent={async (updates) => {
                  // For experts, the saveAgent helper in scope writes
                  // to the Supervisor row by default. We need to PUT
                  // the expert's row instead. Detect and route.
                  const targetId = drawer.payload.agent_id;
                  if (targetId === tree?.agent?.id) {
                    return saveAgent(updates);
                  }
                  // Direct expert update — bypasses the Supervisor's
                  // expected_tree_version check (experts have their
                  // own row + version). Using agentApi.update with
                  // the expert's own version for consistency.
                  const expert = (tree?.experts || []).find(e => e.id === targetId);
                  return agentApi.update(targetId, updates, {
                    expectedTreeVersion: expert?.tree_version,
                    expectedUpdatedAt: expert?.updated_at,
                  }).then(() => setRefetchTick(t => t + 1));
                }}
                canEdit={canEditAgent(currentUser?.role)}
              />
            ) : (
              <pre style={{ fontSize: 11, color: COLORS.text, overflow: "auto", whiteSpace: "pre-wrap" }}>
                {JSON.stringify(drawer.payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab bodies (B6 stubs) ──────────────────────────────────
// Real content arrives in B7 (Overview), B8 (Graph), B9 (Pipeline),
// B10 (Wiring), B11 (Activity), B13 (Knowledge).

export function TabBody({ tab, tree, currentUser, onOpenDrawer, saveAgent, onSelectAgent, onChannelsChanged, setActiveTab }) {
  const empty = (label) => (
    <div style={{ color: COLORS.textDim, fontSize: 12 }}>
      {`<${label}> — placeholder; filled in by a later Phase B item.`}
    </div>
  );
  // Skills v2: the "Needs X · Connect" chip deep-links to the Wiring tab.
  if (tab === "overview")  return <OverviewTab tree={tree} onOpenDrawer={onOpenDrawer} saveAgent={saveAgent} currentUser={currentUser} onSelectAgent={onSelectAgent} onNavigateWiring={() => setActiveTab("wiring")} />;
  if (tab === "graph")     return <GraphTab tree={tree} onOpenDrawer={onOpenDrawer} saveAgent={saveAgent} />;
  if (tab === "pipeline")  return <PipelineTab tree={tree} currentUser={currentUser} saveAgent={saveAgent} />;
  // ASC-v3 RH-UI: per-expert routing examples (exemplars_json), the same field
  // voice warm-handoff + chat L1.5 both read — supervisor-only surface.
  if (tab === "routing")   return <RoutingExamplesPanel agent={tree.agent} experts={tree.experts || []} canEdit={canEditAgent(currentUser?.role)} />;
  if (tab === "wiring")    return <WiringTab tree={tree} saveAgent={saveAgent} currentUser={currentUser} onChannelsChanged={onChannelsChanged} />;
  if (tab === "activity")  return <ActivityTab tree={tree} currentUser={currentUser} onOpenDrawer={onOpenDrawer} />;
  if (tab === "knowledge") return <KnowledgeTab tree={tree} currentUser={currentUser} onOpenDrawer={onOpenDrawer} saveAgent={saveAgent} />;
  if (tab === "lifecycle") return <LifecycleTab tree={tree} />;
  if (tab === "versions")  return <VersionsTab tree={tree} currentUser={currentUser} />;
  return empty(tab);
}

